import { Grid, InputAdornment } from "@mui/material";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { ReactComponent as Trophy01Icon } from "components/Icons/trophy-01.svg";
import { FormulaPreview } from "components/Layout/Campaign/Common/CampaignRules/Rule/Effects/EffectReadOnly";
import { InfoBox } from "components/Layout/InfoBox";
import { useMessage } from "hooks/intl";
import {
  AchievementAggregation,
  AchievementAggregationUnion,
  AchievementCompleteRulePeriodUnion,
  AchievementTriggers,
  AchievementTriggersUnion,
  CompleteRule,
} from "types/achievements";
import { formatNumberValue } from "utilities/numbers";
import { pickPluralizedUnit, pluralizeValueAdornment } from "utilities/strings";
import { useStyles } from "../RuleGoal.styles";
import { getPluralizedTimeframe, getValueAdornment } from "../utils";

interface Props {
  activeRule: number;
  completeRule?: CompleteRule & {
    draft?: CompleteRule;
    isEditing?: boolean;
  };
  errors: any;
  fieldName: string;
  streakType: AchievementCompleteRulePeriodUnion;
  trigger: AchievementTriggersUnion;
  aggregation: {
    type: AchievementAggregationUnion;
    rule?: string;
  };
  event?: string;
}

export const Consecutive = ({
  activeRule,
  completeRule,
  errors,
  fieldName,
  streakType,
  trigger,
  aggregation,
  event,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();

  const { EXPRESSION, QUANTITY } = AchievementAggregation;
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;

  const periodGoal = completeRule?.periodGoal;
  const periodConsecutive = completeRule?.period?.consecutive;

  const pluralizedTransaction = message({
    id: pickPluralizedUnit(periodGoal as number, "transaction", "transactions"),
  }).toLocaleLowerCase();

  const pluralizedConsecutiveType = message({
    id: pickPluralizedUnit(
      periodConsecutive as number,
      streakType,
      getPluralizedTimeframe(streakType),
    ),
  }).toLocaleLowerCase();

  const pluralizedTime = message({
    id: pickPluralizedUnit(periodGoal as number, "time", "times"),
  }).toLocaleLowerCase();

  const periodType = message({ id: streakType }).toLocaleLowerCase();

  const expressionFormula = aggregation?.rule ? (
    <span className={classes.formulaBox}>
      <FormulaPreview pointRule={aggregation?.rule} />
    </span>
  ) : (
    ""
  );

  const eventElement = !!event ? (
    <span className={classes.formulaBox}>{event}</span>
  ) : (
    ""
  );

  const getReadOnlyGoal = () => {
    switch (true) {
      case trigger === TRANSACTION && aggregation?.type === QUANTITY:
        return message(
          { id: "consecutiveTransactionQuantityGoal" },
          {
            periodGoal: formatNumberValue(periodGoal, ""),
            pluralizedTransaction,
            periodType,
            consecutiveElement: formatNumberValue(periodConsecutive, ""),
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => (
              <span className={classes.bolded}>{chunks}</span>
            ),
          },
        );
      case trigger === TRANSACTION && aggregation.type === EXPRESSION:
        return message(
          { id: "consecutiveTransactionExpressionGoal" },
          {
            expressionFormula,
            periodGoal: formatNumberValue(periodGoal, ""),
            periodType,
            consecutiveElement: formatNumberValue(periodConsecutive, ""),
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => (
              <span className={classes.bolded}>{chunks}</span>
            ),
          },
        );
      case trigger === CUSTOM_EVENT && aggregation.type === QUANTITY:
        return message(
          { id: "consecutiveCustomEventQuantityGoal" },
          {
            event: eventElement,
            periodGoal: formatNumberValue(periodGoal, ""),
            pluralizedTime,
            periodType,
            consecutiveElement: formatNumberValue(periodConsecutive, ""),
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => (
              <span className={classes.bolded}>{chunks}</span>
            ),
          },
        );
      case trigger === CUSTOM_EVENT && aggregation.type === EXPRESSION:
        return message(
          { id: "consecutiveCustomEventExpressionGoal" },
          {
            event: eventElement,
            expressionFormula,
            periodGoal: formatNumberValue(periodGoal, ""),
            periodType,
            consecutiveElement: formatNumberValue(periodConsecutive, ""),
            pluralizedConsecutiveType,
            span: (chunks: React.ReactNode[]) => (
              <span className={classes.bolded}>{chunks}</span>
            ),
          },
        );
    }
  };
  return (
    <>
      <Grid item xs={6}>
        <NumberFormatInput
          allowDecimals
          data-test-id={`rule-${activeRule}-goal-input`}
          label={
            <RequiredFieldLabel
              text={message({
                id:
                  aggregation.type === QUANTITY
                    ? trigger === AchievementTriggers.TRANSACTION
                      ? "numberOfTransactionsGoal"
                      : "numberOfCustomEventsGoal"
                    : "goal",
              })}
            />
          }
          name={`${fieldName}.periodGoal`}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {message({
                  id:
                    aggregation.type === QUANTITY
                      ? getValueAdornment(streakType)
                      : "attributeValue",
                }).toLowerCase()}
              </InputAdornment>
            ),
          }}
          error={errors?.completeRule?.periodGoal}
        />
      </Grid>
      <Grid item xs={6}>
        <NumberFormatInput
          label={
            <RequiredFieldLabel
              text={message({
                id: "streakPeriodValue",
              })}
            />
          }
          name={`${fieldName}.period.consecutive`}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {message(
                  {
                    id: "periodInARow",
                  },
                  {
                    period: pluralizeValueAdornment(
                      streakType,
                      completeRule?.period?.consecutive,
                    ),
                  },
                ).toLowerCase()}
              </InputAdornment>
            ),
          }}
          data-test-id={`rule-${activeRule}-goal-consecutive-input`}
          error={errors?.completeRule?.period?.consecutive}
        />
      </Grid>
      <Grid item xs={12}>
        <InfoBox
          type="info"
          text={getReadOnlyGoal() || ""}
          customIcon={<Trophy01Icon />}
          className={classes.infoBox}
        />
      </Grid>
    </>
  );
};
